// src/components/AuthComponent.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap styles
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';

import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import FAQPage from './FAQ';
import './landing.css'
var FormData = require('form-data');

const getOrdinalNum = (n) => {
  return n + (n > 0 ? ['th', 'st', 'nd', 'rd'][(n > 3 && n < 21) || n % 10 > 3 ? 0 : n % 10] : '');
};

const formatDateToEnglish = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  const dateObj = new Date(year, month - 1, day);
  const monthName = dateObj.toLocaleString('en-US', { month: 'long' });
  return `${getOrdinalNum(parseInt(day))} ${monthName}, ${year}`;
};

const calculateAgeAsOn = (dobValue) => {
  if (!dobValue) return { text: '', isValid: true };

  const [birthYear, birthMonth, birthDay] = dobValue.split('-').map(Number);

  const refYear = 2026;
  const refMonth = 1;
  const refDay = 1;

  if (!birthYear || !birthMonth || !birthDay) {
    return { text: '', isValid: false };
  }

  // Compare using plain numeric dates to avoid timezone issues
  const dobNumber = birthYear * 10000 + birthMonth * 100 + birthDay;
  const refNumber = refYear * 10000 + refMonth * 100 + refDay;

  if (dobNumber > refNumber) {
    return {
      text: 'Date of Birth cannot be after 01/01/2026',
      isValid: false,
    };
  }

  let years = refYear - birthYear;
  let months = refMonth - birthMonth;
  let days = refDay - birthDay;

  if (days < 0) {
    months -= 1;

    // Last day of the month before reference month
    const previousMonthLastDate = new Date(refYear, refMonth - 1, 0).getDate();
    days += previousMonthLastDate;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return {
    text: `${years} years, ${months} months, ${days} days`,
    isValid: true,
  };
};

const checkAgeEligibility = (
  dobValue,
  category,
  specialCategory = "None",
  esm = "noEsm",
  esmYears = 0,
  esmMonths = 0,
  esmDays = 0
) => {
  if (!dobValue) {
    return {
      eligible: null,
      message: 'Age Eligibility: Enter DOB',
    };
  }

  const ageData = calculateAgeAsOn(dobValue);

  if (!ageData.isValid) {
    return {
      eligible: false,
      message: 'Invalid Date of Birth',
    };
  }

  // Extract actual years, months, days
  const match = ageData.text.match(/(\d+) years, (\d+) months, (\d+) days/);

  if (!match) {
    return {
      eligible: false,
      message: 'Unable to calculate age',
    };
  }

  let years = parseInt(match[1]);
  let months = parseInt(match[2]);
  let days = parseInt(match[3]);

  // 1. Apply ESM Deduction if candidate is an Ex-Serviceman
  if (esm == "esm") {
    const sYears = parseInt(esmYears) || 0;
    const sMonths = parseInt(esmMonths) || 0;
    const sDays = parseInt(esmDays) || 0;

    // Deduct service period plus the additional 3 years benefit
    years = years - sYears - 3;
    months = months - sMonths;
    days = days - sDays;

    // Handle borrowing logic for negative numbers resulting from subtraction
    if (days < 0) {
      months--;
      days += 30; // Standardized month breakdown for relative math deduction
    }
    if (months < 0) {
      years--;
      months += 12;
    }
  }

  // ST applicants above 33 can register for Sweeper up to age 45.
  // These limits use the effective age after the existing ESM deduction.
  const maxAge = category === 'ST' ? 45 : 33;

  // 3. Minimum age check
  if (!(esm === "esm") && years < 18) {
    return {
      eligible: false,
      message: esm === "esm"
        ? `Not Eligible: Effective age (${years} years) is below the minimum 18 years required as on 01/01/2026`
        : 'Not Eligible: Minimum age should be 18 years as on 01/01/2026',
    };
  }

  // 4. Maximum age check
  if (
    years > maxAge ||
    (years === maxAge && (months > 0 || days > 0))
  ) {
    const roleLabel = `${category} category`;
    return {
      eligible: false,
      message: esm === "esm"
        ? `Not Eligible: Maximum effective age for ${roleLabel} is ${maxAge} years. Your calculated effective age is ${years} years, ${months} months, ${days} days as on 01/01/2026`
        : `Not Eligible: Maximum age for ${roleLabel} is ${maxAge} years as on 01/01/2026`,
    };
  }

  if (category === 'ST' && (years > 33 || (years === 33 && (months > 0 || days > 0)))) {
    return {
      eligible: true,
      sweeperOnly: true,
      message: `Eligible for the Sweeper post only! Your ${esm === 'esm' ? 'effective age after service deduction' : 'age'} is above 33 years and within the maximum of 45 years as on 01/01/2026. Please verify your Date of Birth carefully; once submitted, it cannot be edited.`,
    };
  }

  return {
    eligible: true,
    message: esm === "esm"
      ? `Eligible: Age criteria satisfied (Effective ESM Age: ${years} years, ${months} months, ${days} days)`
      : `Eligible: Age criteria satisfied for ${category} category`,
  };
};


const AuthComponent = () => {
  const navigate = useNavigate();
  const [isLogin, setLogin] = useState(true);
  const [page, setPage] = useState(1);
  const [isRegistered, setRegistered] = useState(false);
  const [isSubmit, setSubmit] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [altPhone, setAtlPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [mName, setmName] = useState('');
  const [lName, setlName] = useState('');
  const [fName, setfName] = useState('');
  const [dob, setDob] = useState('');
  const [fullAddress, setfullAddress] = useState('');
  const [city, setCity] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [state, setState] = useState('Ladakh');
  const [district, setDistrict] = useState('');
  const [petCenter, setPetCenter] = useState('');
  const [writtenCenter, setWrittenCenter] = useState('');
  const [gender, setGender] = useState('');
  const [category, setCategory] = useState('ST');
  const [specialCategory, setSpecialCategory] = useState('None');
  const [esm, setEsm] = useState("noEsm");
  const [esmYears, setEsmYears] = useState("");
  const [esmMonths, setEsmMonths] = useState("");
  const [esmDays, setEsmDays] = useState("");
  const [qualification, setQualification] = useState('10th Pass');
  const [bonus, setBonus] = useState('noAQT');
  const [email, setEmail] = useState('');
  const [show, setShow] = useState(false);
  const [profile, setProfile] = useState(null);

  // File States
  const [photoFile, setPhotoFile] = useState(null);
  const [dobFile, setDobFile] = useState(null);
  const [domFile, setDomFile] = useState(null);
  const [educationFile, setEducationFile] = useState(null);
  const [stFile, setStFile] = useState(null);
  const [specialCategoryFile, setSpecialCategoryFile] = useState(null);
  const [bonusFile, setBonusFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  const [signature, setSignature] = useState(null);
  const [showLoginOTP, setshowLoginOTP] = useState(false);
  const [isChecked1, setIsChecked1] = useState(false);
  const [isChecked2, setIsChecked2] = useState(false);
  const [isChecked3, setIsChecked3] = useState(false);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [criminal, setCriminal] = useState("No");
  const [criminalDetails, setCriminalDetails] = useState("");

  const ageAsOn = calculateAgeAsOn(dob);
  const ageEligibility = checkAgeEligibility(dob, category, specialCategory, esm, esmYears, esmMonths, esmDays);

  const isPhoneValid = /^[0-9]{10}$/.test(phoneNumber);
  const phoneError = (phoneTouched || phoneNumber !== '') && !isPhoneValid
    ? 'Mobile number must contain exactly 10 digits (0–9).'
    : '';

  const validatePhoneNumber = () => {
    setPhoneTouched(true);
    if (!isPhoneValid) {
      toast.error('Mobile number must contain exactly 10 digits (0–9).');
      document.getElementById('phoneNumber')?.focus();
      return false;
    }
    return true;
  };

  const phoneInputProps = {
    type: 'tel',
    inputMode: 'numeric',
    autoComplete: 'tel-national',
    id: 'phoneNumber',
    value: phoneNumber,
    pattern: '[0-9]{10}',
    required: true,
    'aria-invalid': !!phoneError,
    'aria-describedby': 'phoneNumberError',
    onChange: (e) => setPhoneNumber(e.target.value),
    onBlur: () => setPhoneTouched(true),
    onInvalid: (e) => {
      // Use our inline message instead of browser-specific validation popups.
      e.preventDefault();
      setPhoneTouched(true);
      e.target.focus();
    },
  };

  const handleLoginClick = () => {
    setLogin(true);
    setSubmit(false);
    setPage(0);
  };
  const handleFAQClick = () => {
    console.log("faq page")
    setPage(2);
  };
  const handleInstructionsClick = () =>{
    console.log("How to apply page")
    setPage(3);
  }

  const handleRegisterClick = () => {
    setLogin(false);
    setRegistered(true);
    setPage(1);
  };

  const generateCaptcha = () => {
    let num1 = Math.floor(Math.random() * 10);
    let num2 = Math.floor(Math.random() * 10);
    const operator = ['+', '-', '*'][Math.floor(Math.random() * 3)];

    // If the operator is subtraction, ensure num1 is greater than or equal to num2
    if (operator === '-' && num1 < num2) {
      const temp = num1;
      num1 = num2;
      num2 = temp;
    }

    const question = `What is ${num1} ${operator} ${num2}?`;

    // Safe calculation without using eval
    let answer;
    if (operator === '+') answer = num1 + num2;
    else if (operator === '-') answer = num1 - num2;
    else if (operator === '*') answer = num1 * num2;

    setCaptchaAnswer(answer.toString());

    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    ctx.font = '20px Arial';
    ctx.fillText(question, 10, 30);
    const dataURL = canvas.toDataURL();
    setCaptchaImage(dataURL);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  // --- FILE HANDLERS ---
  const handlephotoFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const fileSize = event.target.files[0].size;
      if(fileSize > 524288 ){
        toast.error("Selected file size is " + formatFileSize(fileSize) + ". Please choose a file less than 512 KB")
        event.target.value = ""
        setPhotoFile(null)
      }else{
        setProfile(URL.createObjectURL(event.target.files[0]));
        setPhotoFile(event.target.files[0]);
      }
    }
  };

  const handleeducationFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      if(event.target.files[0].size > 524288 ){
        toast.error("Please choose a file less than 512kb")
        event.target.value = ""
        setEducationFile(null)
      }else{
        setEducationFile(event.target.files[0]);
      }
    }
  };

  const handledobfilechange = (event) => {
    if (event.target.files && event.target.files[0]) {
        if(event.target.files[0].size > 524288 ){
          toast.error("Please choose a file less than 512kb")
          event.target.value = ""
          setDobFile(null)
        }else{
          setDobFile(event.target.files[0]);
        }
    }
  };

  const handledomfilechange = (event) => {
    if (event.target.files && event.target.files[0]) {
        if(event.target.files[0].size > 524288 ){
          toast.error("Please choose a file less than 512kb")
          event.target.value = ""
          setDomFile(null)
        }else{
          setDomFile(event.target.files[0]);
        }
    }
  };

  const handlesignaturefilechange = (event) => {
    if (event.target.files && event.target.files[0]) {
      if(event.target.files[0].size > 524288){
        toast.error("Please choose a file less than 512kb")
        event.target.value = ""
        setSignatureFile(null)
      }else{
        setSignature(URL.createObjectURL(event.target.files[0]));
        setSignatureFile(event.target.files[0]);
      }
    }
  };

  const handleSTfilechange = (event) => {
    if (event.target.files && event.target.files[0]) {
      if(event.target.files[0].size > 524288 ){
        toast.error("Please choose a file less than 512kb")
        event.target.value = ""
        setStFile(null)
      }else{
        setStFile(event.target.files[0]);
      }
    }
  };

  const handleSpecialCategoryFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      if (event.target.files[0].size > 524288) {
        toast.error("Please choose a file less than 512kb");
        event.target.value = "";
        setSpecialCategoryFile(null);
      } else {
        setSpecialCategoryFile(event.target.files[0]);
      }
    }
  };

  const handleBonusfilechange = (event) => {
    if (event.target.files && event.target.files[0]) {
      if(event.target.files[0].size > 524288 ){
        toast.error("Please choose a file less than 512kb")
        event.target.value = ""
        setBonusFile(null)
      }else{
        setBonusFile(event.target.files[0]);
      }
    }
  };

  const uploadFileToS3 = async (presignedPostData, file) => {
    try {
      const formData = new FormData();
      Object.keys(presignedPostData.fields).forEach(key => {
        formData.append(key, presignedPostData.fields[key]);
      });

      formData.append("file", file);

      const response = await fetch(presignedPostData.url, {
        method: 'POST',
        body: formData
      });

      return response.ok;

    } catch (error) {
      console.error("Upload failed at network level:", error);
      return false;
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const handleOTPClose = () => setshowLoginOTP(false);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhoneNumber()) return;
    if(otp.length === 0){
      toast.error("Press Login button")
      return
    }
    console.log('Logging in with Phone Number:', phoneNumber, 'and OTP:', otp);
    try {
      const response = await axios.post('/api/verify', {
        phone: phoneNumber,
        otp: otp,
      });

      console.log('OTP Verification Response:', response.data);
      if(response.data.message === "Match" ){
        console.log("SUCKS! You've been signed in!")
        navigate("/home");
      }else{
        toast.error("Incorrect OTP");
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleGenerateOTP = async (e) => {
    if (!validatePhoneNumber()) return;
    if(userAnswer !== captchaAnswer){
      toast.error("Incorrect captcha")
      return
    }

    try {
      const response = await axios.post('/api/sendOTP', {
        phone: phoneNumber,
      });

      console.log('OTP Request Response:', response.data);
      setshowLoginOTP(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const resendOTP = async (e) =>{
    if (!validatePhoneNumber()) return;
    try {
      const response = await axios.post('/api/sendOTP', {
        phone: phoneNumber,
      });
    }catch(error){
      console.log(error)
    }
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhoneNumber()) return;
    if(userAnswer !== captchaAnswer){
      toast.error("Incorrect captcha")
      return
    }

    if (!gender) {
      alert('Please select your Sex.');
      return;
    }
    if (!ageAsOn.isValid) {
      toast.error("Please select a valid Date of Birth.");
      return;
    }
    if (!ageEligibility.eligible) {
      toast.error("You are not eligible as per age criteria.");
      return;
    }

    try {
      const response = await axios.post('/api/sendOTP', {
        phone: phoneNumber,
      });

      console.log('OTP Request Response:', response.data);
      handleShow();
      setSubmit(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };


  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhoneNumber()) return;

    try {
      // 1. VERIFY OTP
      const verifyResponse = await axios.post('/api/verify', {
        phone: phoneNumber,
        otp: otp,
      });

      // Early return if OTP is incorrect
      if (verifyResponse.data.message !== "Match") {
        toast.error("Incorrect OTP");
        return;
      }

      // 2. PREPARE UPLOADS
      setUploadMessage("Uploading.... Please do not refresh or go back.");
      const { data: presignedPostData } = await axios.get('/api/getUrls');

      // Create an array of only the files that actually exist
      const filesToUpload = [
        { file: photoFile, url: presignedPostData.photo },
        { file: dobFile, url: presignedPostData.dob },
        { file: domFile, url: presignedPostData.dom },
        { file: educationFile, url: presignedPostData.education },
        { file: stFile, url: presignedPostData.st },
        { file: specialCategoryFile, url: presignedPostData.specialCategory },
        { file: bonusFile, url: presignedPostData.bonus },
        { file: signatureFile, url: presignedPostData.signature },
      ].filter(item => item.file !== null && item.file !== undefined && item.url);

      // 3. EXECUTE PARALLEL UPLOADS
      const uploadPromises = filesToUpload.map(item => uploadFileToS3(item.url, item.file));
      const uploadResults = await Promise.all(uploadPromises);

      // Check if any of the uploads returned a falsy value (failed)
      const allUploadedSuccessfully = uploadResults.every(res => res);

      if (!allUploadedSuccessfully) {
        toast.error("Error in Uploading Documents. Please try registering again.");
        return;
      }

      // 4. REGISTER USER
      const registerPayload = {
        phone: phoneNumber, Name: name, mName, lName, fName, dob,
        fullAddress, city, zipcode, state, petCenter, writtenCenter,
        district, gender, qualification, category, specialCategory, bonus, esm,
        esmYears, esmMonths, esmDays, email, altPhone, criminal, criminalDetails
      };

      const registerResponse = await axios.post('/api/register', registerPayload);

      if (registerResponse.data === "Error in register") {
        toast.error("User already registered with this Phone Number");
        handleClose();
        handleLoginClick();
        return;
      }

      // 5. SUCCESSFUL REGISTRATION CLEANUP
      console.log("You've been signed in!");
      setRegistered(true);
      setSubmit(false);
      setPhoneNumber("");
      setOtp("");
      setLogin(true);
      navigate("/home");

    } catch (error) {
      console.error('Error during submission:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      // Always clear the loading message whether it succeeded or failed
      setUploadMessage("");
    }
  };


  useEffect(() => {
    generateCaptcha();
  }, []);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px',
          backgroundColor: '#06213f',
        }}
      >
        <img
          src="/logo_0.png"
          alt="Header Image"
          style={{
            maxWidth: '100%',
            height: 'auto',
            maxHeight: '100px',
            objectFit: 'contain',
            backgroundColor: '#06213f',
          }}
        />
      </div>

      <Navbar collapseOnSelect expand="lg" variant="dark" style={{ backgroundColor: '#06213f' }}>
        <Container>
          <Navbar.Brand href="/">Ladakh Fire Services Recruitment 2026</Navbar.Brand>

          <Navbar.Toggle aria-controls="responsive-navbar-nav" />

          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link onClick={handleRegisterClick}>Register</Nav.Link>
              <Nav.Link onClick={handleLoginClick}>Login</Nav.Link>
              <Nav.Link href="https://police.ladakh.gov.in/pages/pdf/fire_recruitment.pdf" target="_blank" rel="noopener noreferrer">
                Advertisement
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {(page === 0) ? (
        <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#dee0d7', padding: '50px', height: 'auto' }}>
          <Card className="shadow-sm" style={{ borderColor: "#000", maxWidth: "60rem", width: '100%', height: '50%'}}>
          <Card.Body>
            <br></br>
            <br></br>
        <form onSubmit={handleLoginSubmit} className="w-50 mx-auto">
          <center>
            <h2 className="mb-4" style={{ fontWeight: 'bold' }}>Login</h2>
          </center>

          <div className="mb-3">
            <label htmlFor="phoneNumber" className="form-label">Mobile Number:</label>
            <input {...phoneInputProps} className={`form-control${phoneError ? ' is-invalid' : ''}`} />
            <div id="phoneNumberError" className="text-danger small mt-1" aria-live="polite" aria-atomic="true">{phoneError}</div>
            <br/>
            <label className="form-label">
              CAPTCHA:
              {captchaImage && <img src={captchaImage} alt="CAPTCHA" />}
              <input
                type="text"
                value={userAnswer}
                placeholder="Enter captcha answer"
                onChange={(e) => setUserAnswer(e.target.value)}
                required
              />
            </label>
            <Button
              variant="primary"
              onClick={handleGenerateOTP}
              style={{ backgroundColor: '#06213f'}}
            >
              {'Login'}
            </Button>
          </div>

          <Modal show={showLoginOTP} onHide={handleOTPClose}>
            <Modal.Header closeButton>
              <Modal.Title>Enter OTP:</Modal.Title>
            </Modal.Header>
            <Modal.Body>Enter the OTP sent on {phoneNumber}
            <div><Button variant="outline-primary" size="sm"
            style={{
              border: '1px solid #06213f',
              color: '#06213f',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#06213f';
              e.target.style.borderColor = '#06213f';
              e.target.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderColor = '#06213f';
              e.target.style.color = '#06213f';
            }}
            onClick={resendOTP}>
                    Resend OTP
                  </Button>{' '}
            </div>
              <div className="mb-3">
                <label htmlFor="otp" className="form-label">OTP:</label>
                <input type="text" className="form-control" id="otp" value={otp} onChange={(e) => setOtp(e.target.value)} required />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleOTPClose}>
                Close
              </Button>
              <Button variant="primary" onClick={handleLoginSubmit} style={{ backgroundColor: '#06213f'}}>
                Submit
              </Button>
            </Modal.Footer>
          </Modal>
        </form>
        </Card.Body>
        </Card>
        </div>
      ) : page === 2 ? (
        <div className="text-center">
          <FAQPage/>
        </div>
      ) :(
       <div className="min-vh-100 d-flex justify-content-center py-5" style={{ backgroundColor: '#dee0d7' }}>
  <Card className="shadow-lg border-0 rounded-4" style={{ maxWidth: "1000px", width: '100%', margin: '0 15px' }}>
    <Card.Body className="p-4 p-md-5">

      <form onSubmit={handleRegisterSubmit} className="mx-auto">
        <div className="text-center mb-5">
          <h2 className="fw-bold mb-2" style={{ color: '#06213f' }}>Registration Form</h2>
          <p className="text-muted">Please fill out the details carefully.</p>
        </div>

        {/* --- SECTION: PHOTO UPLOAD --- */}
        <div className="d-flex flex-column align-items-center mb-5 pb-4 border-bottom">
          {profile && (
            <div className="position-relative mb-3" style={{ width: '150px', height: '180px' }}>
              <img
                alt="Profile Preview"
                src={profile}
                className="img-thumbnail shadow-sm object-fit-cover w-100 h-100"
              />
            </div>
          )}

          <div className="text-center w-100" style={{ maxWidth: '300px' }}>
            <label className="form-label fw-semibold">1. Upload Passport Photo</label>
            <input type="file" className="form-control form-control-sm" accept="image/jpeg" onChange={(e) => handlephotoFileChange(e)} required />
            <div className="form-text mt-1">(Max 512kb allowed; jpg/jpeg only)</div>
          </div>
        </div>

        {/* --- SECTION: PERSONAL DETAILS --- */}
        <h5 className="fw-bold text-uppercase mb-4" style={{ color: '#06213f' }}>Personal Details</h5>

        <div className="row g-4 mb-4">
          <div className="col-12">
            <label className="form-label fw-semibold">2. Full Name</label>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <input type="text" className="form-control bg-light" id="name" placeholder="First Name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="col-12 col-md-4">
                <input type="text" className="form-control bg-light" id="mName" placeholder="Middle Name (Optional)" value={mName} onChange={(e) => setmName(e.target.value)} />
              </div>
              <div className="col-12 col-md-4">
                <input type="text" className="form-control bg-light" id="lName" placeholder="Last Name" value={lName} onChange={(e) => setlName(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <label htmlFor="fname" className="form-label fw-semibold">3. Father's Name</label>
            <input type="text" className="form-control bg-light" id="fname" value={fName} onChange={(e) => setfName(e.target.value)} required />
          </div>

          <div className="col-md-6">
            <label htmlFor="dob" className="form-label fw-semibold">4. Date of Birth</label>
            <input type="date" className="form-control bg-light" id="dob" value={dob} max="2026-06-01" onChange={(e) => setDob(e.target.value)} required />
            {dob && (
              <div className="form-text text-dark fw-bold mt-1">
                Selected Date: {formatDateToEnglish(dob)}
              </div>
            )}
            {ageAsOn.text && (
              <div className="form-text text-primary fw-medium mt-1">
                Age as on 01/01/2026: {ageAsOn.text}
              </div>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold d-block">5. Gender</label>
            <div className="form-check form-check-inline mt-2">
              <input className="form-check-input" type="radio" id="male" name="gender" value="male" onChange={(e) => setGender(e.target.value)} />
              <label className="form-check-label" htmlFor="male">Male</label>
            </div>
            <div className="form-check form-check-inline mt-2">
              <input className="form-check-input" type="radio" id="female" name="gender" value="female" onChange={(e) => setGender(e.target.value)} />
              <label className="form-check-label" htmlFor="female">Female</label>
            </div>
          </div>
        </div>

        {/* --- SECTION: CONTACT & ADDRESS --- */}
        <h5 className="fw-bold text-uppercase mb-4 mt-5 border-top pt-4" style={{ color: '#06213f' }}>Contact & Address</h5>

        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <label htmlFor="email" className="form-label fw-semibold">6. Email Address</label>
            <input type="email" className="form-control bg-light" id="email" value={email} style={{ textTransform: 'uppercase'}} onChange={(e) => setEmail(e.target.value)} required/>
          </div>

          <div className="col-md-6">
            <label htmlFor="phoneNumber" className="form-label fw-semibold">7. Mobile Number</label>
            <input {...phoneInputProps} className={`form-control bg-light${phoneError ? ' is-invalid' : ''}`} />
            <div id="phoneNumberError" className="text-danger small mt-1" aria-live="polite" aria-atomic="true">{phoneError}</div>
          </div>

          <div className="col-md-6">
            <label htmlFor="altPhone" className="form-label fw-semibold">8. Alternate Mobile Number (Optional)</label>
            <input type="tel" className="form-control bg-light" id="altPhone" value={altPhone} onChange={(e) => setAtlPhone(e.target.value.replace(/\D/g, ''))} pattern="[0-9]{10}" maxLength="10" title="Please enter a 10-digit mobile number" />
          </div>

          <div className="col-12">
            <label htmlFor="fullAddress" className="form-label fw-semibold">9. Permanent Address</label>
            <input type="text" className="form-control bg-light" id="fullAddress" value={fullAddress} onChange={(e) => setfullAddress(e.target.value)} required />
          </div>

          <div className="col-md-6">
            <label htmlFor="city" className="form-label fw-semibold">10. City/Town/Village</label>
            <input type="text" className="form-control bg-light" id="city" value={city} onChange={(e) => setCity(e.target.value)} required />
          </div>

          <div className="col-md-6">
            <label htmlFor="zipcode" className="form-label fw-semibold">11. Pincode</label>
            <input type="text" pattern="[0-9]{6}" title="Six digit zip code" className="form-control bg-light" id="zipcode" value={zipcode} onChange={(e) => setZipcode(e.target.value)} required />
          </div>

          <div className="col-md-6">
            <label htmlFor="district" className="form-label fw-semibold">12. District</label>
            <select className="form-select bg-light" id="district" value={district} onChange={(e) => setDistrict(e.target.value)} required>
              <option value="">Select District...</option>
              <option value="Leh">Leh</option>
              <option value="Nubra">Nubra</option>
              <option value="Sham">Sham</option>
              <option value="Changthang">Changthang</option>
              <option value="Kargil">Kargil</option>
              <option value="Zanskar">Zanskar</option>
              <option value="Drass">Drass</option>
            </select>
          </div>

          <div className="col-md-6">
            <label htmlFor="state" className="form-label fw-semibold">13. State/UT</label>
            <input type="text" className="form-control bg-light text-muted" id="state" value="Ladakh" readOnly required />
            <div className="form-text">Note: Only domiciles of UT of Ladakh are eligible.</div>
          </div>

        </div>

        {/* --- SECTION: EXAM PREFERENCES --- */}
        <h5 className="fw-bold text-uppercase mb-4 mt-5 border-top pt-4" style={{ color: '#06213f' }}>Exam Preferences</h5>

        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <label htmlFor="petCenter" className="form-label fw-semibold">14. Preferred Center for Physical Test</label>
            <select className="form-select bg-light" id="petCenter" value={petCenter} onChange={(e) => setPetCenter(e.target.value)} required>
              <option value="">Select Center...</option>
              <option value="Leh">Leh</option>
              <option value="Kargil">Kargil</option>
            </select>
          </div>

          <div className="col-md-6">
            <label htmlFor="writtenCenter" className="form-label fw-semibold">15. Preferred Center for Written Test</label>
            <select className="form-select bg-light" id="writtenCenter" value={writtenCenter} onChange={(e) => setWrittenCenter(e.target.value)} required>
              <option value="">Select Center...</option>
              <option value="Leh">Leh</option>
              <option value="Kargil">Kargil</option>
            </select>
          </div>
        </div>

        {/* --- SECTION: QUALIFICATIONS & ELIGIBILITY --- */}
        <h5 className="fw-bold text-uppercase mb-4 mt-5 border-top pt-4" style={{ color: '#06213f' }}>Qualifications & Eligibility</h5>

        <div className="row g-4 mb-4">
          {/* Category */}
          <div className="col-md-6">
            <label htmlFor="category" className="form-label fw-semibold">16. Category</label>
            <select className="form-select bg-light" id="category" value={category} onChange={(e) => { setCategory(e.target.value); setStFile(null); }} required>
              <option value="ST">ST</option>
              <option value="EWS">EWS</option>
            </select>

              <div className="mt-3 p-3 bg-white border border-primary shadow-sm rounded-3">
                <label className="form-label fw-semibold">16a. Category Certificate <span className="text-muted fw-normal d-block small">(Max 512kb; pdf only)</span></label>
                <input type="file" className="form-control" accept="application/pdf" key={category} onChange={handleSTfilechange} required/>
              </div>
          </div>

          {/* Special Category */}
          <div className="col-md-6">
            <label htmlFor="specialCategory" className="form-label fw-semibold">17. Special Category for Horizontal Reservation</label>
            <select className="form-select bg-light" id="specialCategory" value={specialCategory} onChange={(e) => {
              const val = e.target.value;
              setSpecialCategory(val);

              setSpecialCategoryFile(null);

              // Handle ESM implicitly based on dropdown selection
              if (val === 'Ex-Servicemen') {
                setEsm('esm');
              } else {
                setEsm('noEsm');
                setEsmYears(""); setEsmMonths(""); setEsmDays("");
              }

            }} required>
              <option value="None">None</option>
              <option value="Ex-Agniveer">Ex-Agniveer</option>
              <option value="Ex-Servicemen">Ex-Servicemen</option>
            </select>

            {specialCategory !== "None" && (
              <div className="mt-3 p-3 bg-white border border-primary shadow-sm rounded-3">
                <label className="form-label fw-semibold">17a. Special Category Certificate <span className="text-muted fw-normal d-block small">(Max 512kb; pdf only)</span></label>
                <input type="file" className="form-control" accept="application/pdf" key={specialCategory} onChange={handleSpecialCategoryFileChange} required/>
              </div>
            )}
          </div>

          {/* Conditional ESM Fields directly tied to specialCategory */}
          {esm === "esm" && (
            <div className="col-12">
              <div className="card p-4 bg-white border border-primary shadow-sm rounded-3">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label fw-semibold text-dark">
                      17b. Total Qualifying Service Period:
                    </label>
                    <div className="row g-2">
                      <div className="col-4">
                        <div className="form-floating">
                          <select
                            className="form-select bg-light"
                            id="esmYears"
                            value={esmYears}
                            onChange={(e) => setEsmYears(e.target.value)}
                            required
                          >
                            <option value="">Select...</option>
                            {[...Array(46).keys()].map(y => (
                              <option key={y} value={y}>{y} {y === 1 ? 'Year' : 'Years'}</option>
                            ))}
                          </select>
                          <label htmlFor="esmYears">Years</label>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="form-floating">
                          <select
                            className="form-select bg-light"
                            id="esmMonths"
                            value={esmMonths}
                            onChange={(e) => setEsmMonths(e.target.value)}
                            required
                          >
                            <option value="">Select...</option>
                            {[...Array(12).keys()].map(m => (
                              <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                            ))}
                          </select>
                          <label htmlFor="esmMonths">Months</label>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="form-floating">
                          <select
                            className="form-select bg-light"
                            id="esmDays"
                            value={esmDays}
                            onChange={(e) => setEsmDays(e.target.value)}
                            required
                          >
                            <option value="">Select...</option>
                            {[...Array(31).keys()].map(d => (
                              <option key={d} value={d}>{d} {d === 1 ? 'Day' : 'Days'}</option>
                            ))}
                          </select>
                          <label htmlFor="esmDays">Days</label>
                        </div>
                      </div>
                    </div>
                    <div className="form-text text-muted mt-2">
                      Select the exact period of service matching your official Discharge Certificate.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Essential Educational Qualification */}
          <div className="col-md-6">
            <label htmlFor="qualification" className="form-label fw-semibold">18. Essential Educational Qualification</label>
            <select className="form-select bg-light" id="qualification" value={qualification} onChange={(e) => setQualification(e.target.value)} required>
                          <option value="10+2 with Chemistry and Physics">10+2 with Chemistry and Physics</option>
                          <option value="10+2">10+2</option>
              <option value="10th Pass">10th Pass</option>
            </select>
          </div>

          {/* Desired Qualification */}
          <div className="col-md-6">
             <label htmlFor="bonus" className="form-label fw-semibold">19. Desired Qualification: Do you have an LMV driving license with transport endorsement?</label>
            <select className="form-select bg-light" id="bonus" value={bonus} onChange={(e) => { setBonus(e.target.value); setBonusFile(null); }} required>
              <option value="noAQT">No</option>
              <option value="LMV">Yes</option>
            </select>

            {bonus !== "noAQT" && (
              <div className="mt-3 p-3 bg-white border border-primary shadow-sm rounded-3">
                <label className="form-label fw-semibold">19a. Desired Qualification Certificate <span className="text-muted fw-normal d-block small">(Max 512kb; pdf only)</span></label>
                <input type="file" className="form-control" accept="application/pdf" onChange={handleBonusfilechange} required/>
              </div>
            )}
          </div>

          {ageEligibility.message && (
              <div className={ageEligibility.sweeperOnly ? 'col-12 alert alert-warning mt-1' : `col-12 form-text fw-medium mt-1 ${ageEligibility.eligible ? 'text-success' : 'text-danger'}`} role="status">
                {ageEligibility.message}
              </div>
            )}
        </div>


        {/* --- SECTION: CRIMINAL CASE DISCLOSURE --- */}
        <div className="col-12 mt-4">
          <label className="form-label fw-semibold d-block">
            20. Have you ever been involved in any criminal case, whether pending or resulting in conviction?
          </label>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="criminalOptions"
              id="criminalYes"
              checked={criminal === "Yes"}
              onChange={() => setCriminal("Yes")}
            />
            <label className="form-check-label" htmlFor="criminalYes">Yes</label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="criminalOptions"
              id="criminalNo"
              checked={criminal === "No"}
              onChange={() => {
                setCriminal("No");
                setCriminalDetails(""); // Wipe details clear if flipped back to No
              }}
            />
            <label className="form-check-label" htmlFor="criminalNo">No</label>
          </div>
        </div>

        {/* Conditional Criminal Case Details Textarea */}
        {criminal === "Yes" && (
          <div className="col-12 mt-3">
            <div className="card p-4 bg-white border border-danger shadow-sm rounded-3">
              <label htmlFor="criminalDetails" className="form-label fw-semibold text-danger">
                20a. Details of Criminal Case(s):
              </label>
              <textarea
                className="form-control bg-light text-uppercase"
                id="criminalDetails"
                rows="4"
                placeholder="ENTER FIR NUMBER, POLICE STATION NAME, SECTIONS, AND CURRENT STATUS OF THE CASE..."
                value={criminalDetails}
                onChange={(e) => setCriminalDetails(e.target.value)}
                maxLength="1000"
                required
              />
              <div className="form-text text-muted mt-1">
                Please provide comprehensive details. Maximum 1000 characters.
              </div>
            </div>
          </div>
        )}

        {/* --- SECTION: REQUIRED DOCUMENT UPLOADS --- */}
        <h4 className="fw-bold text-uppercase mb-4 mt-5 border-top pt-4" style={{ color: '#06213f' }}>Required Document Uploads</h4>

        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <label className="form-label fw-semibold">21. Upload Proof of Date of Birth (Matriculation) <span className="text-muted fw-normal d-block small">(Max 512kb; pdf only)</span></label>
            <input type="file" accept="application/pdf" className="form-control bg-light" onChange={handledobfilechange} required />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">22. Upload Domicile Certificate of UT Ladakh <span className="text-muted fw-normal d-block small">(Max 512kb; pdf only)</span></label>
            <input type="file" accept="application/pdf" className="form-control bg-light" onChange={handledomfilechange} required />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">
              23. Upload Certificate for Essential Educational Qualification ({qualification}) <span className="text-muted fw-normal d-block small">(Max 512kb; pdf only)</span>
            </label>
            <input type="file" accept="application/pdf" className="form-control bg-light" onChange={handleeducationFileChange} required />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-semibold">24. Upload image of Signature <span className="text-muted fw-normal d-block small">(Max 512kb; jpg/jpeg only)</span></label>
            {signature && (
              <div className="mb-3">
                <img alt="Signature Preview" src={signature} style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }} className="img-thumbnail shadow-sm" />
              </div>
            )}
            <input type="file" accept="image/jpeg" className="form-control bg-light" onChange={handlesignaturefilechange} required />
          </div>
        </div>

        {/* --- SECTION: DECLARATION --- */}
        <h5 className="fw-bold text-uppercase mb-4 mt-5 border-top pt-4" style={{ color: '#06213f' }}>Declaration & Verification</h5>

        <div className="bg-light p-4 rounded-3 border mb-4">
          <div className="form-check mb-3">
            <input className="form-check-input" type="checkbox" id="dec1" checked={isChecked1} onChange={(e) => setIsChecked1(e.target.checked)} />
            <label className="form-check-label text-muted" htmlFor="dec1">
              I declare that I have not been convicted in any criminal case and that no criminal case is pending against me before any court of law. I further declare that no FIR, criminal proceeding, or prosecution requiring disclosure under the recruitment rules has been concealed by me. I understand that suppression of material information or furnishing false information shall render my candidature/appointment liable to cancellation at any stage.
            </label>
          </div>
          <div className="form-check mb-3">
            <input className="form-check-input" type="checkbox" id="dec2" checked={isChecked2} onChange={(e) => setIsChecked2(e.target.checked)} />
            <label className="form-check-label text-muted" htmlFor="dec2">
              I undertake to disclose all criminal cases, whether pending or decided, in the relevant section of the application form. Suppression of any such information shall lead to cancellation of candidature and may invite legal action.
            </label>
          </div>
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="dec3" checked={isChecked3} onChange={(e) => setIsChecked3(e.target.checked)} />
            <label className="form-check-label text-muted" htmlFor="dec3">
              I declare that the information provided above is true and correct to the best of my knowledge and understanding. I understand that if any information is found to be incorrect or false, my candidature may be cancelled at any time.
            </label>
          </div>
        </div>

        <div className="row justify-content-between align-items-end mb-5">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Security CAPTCHA</label>
            <div className="d-flex align-items-center gap-3">
              {captchaImage && <img src={captchaImage} alt="CAPTCHA" className="rounded shadow-sm border" />}
              <input type="text" className="form-control" value={userAnswer} placeholder="Enter text" onChange={(e) => setUserAnswer(e.target.value)} required />
            </div>
          </div>
          <div className="col-md-4 mt-4 mt-md-0 text-md-end">
            <button
              type="submit"
              className="btn btn-lg w-100 shadow-sm text-white"
              style={{ backgroundColor: '#06213f' }}
              disabled={!(isChecked1 && isChecked2 && isChecked3)}>
                Submit Registration
            </button>
          </div>
        </div>
        <div className="alert alert-info mt-4" role="alert">
        <strong>Need Help?</strong><br />
        If you encounter any issues while filling out the application form, please contact us at{" "}
        <a href="mailto:chairman.lpssrb@police.ladakh.gov.in">
          chairman.lpssrb@police.ladakh.gov.in
        </a>.
      </div>

        {/* --- MODAL --- */}
        <Modal show={show} onHide={handleClose} size="lg" centered>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="fw-bold" style={{ color: '#06213f' }}>Confirm Your Details</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p className="text-muted mb-4">Please review the details below. Once submitted, these details cannot be changed.</p>
            {ageEligibility.sweeperOnly && (
              <div className="alert alert-warning" role="status">{ageEligibility.message}</div>
            )}

            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="align-middle">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Field</th>
                    <th>Entered Data</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>1</td><td>Full Name</td><td className="fw-medium">{name.toUpperCase() + " " + mName.toUpperCase() + " " + lName.toUpperCase()}</td></tr>
                  <tr><td>2</td><td>Father's Name</td><td className="fw-medium">{fName.toUpperCase()}</td></tr>
                  <tr>
                    <td>3</td><td>DOB</td>
                    <td>
                      <span className="fw-medium">{formatDateToEnglish(dob)}</span>
                      <div className="text-danger small mt-1">⚠️ Please verify carefully. Cannot be changed later.</div>
                    </td>
                  </tr>
                  <tr><td>4</td><td>Age (01/01/2026)</td><td className="fw-medium">{ageAsOn.text || '-'}</td></tr>
                  <tr><td>5</td><td>Address</td><td className="fw-medium">{fullAddress.toUpperCase()}</td></tr>
                  <tr><td>6</td><td>City & Pincode</td><td className="fw-medium">{city.toUpperCase()} - {zipcode}</td></tr>
                  <tr>
                    <td>7</td><td>District & State</td>
                    <td>
                      <span className="fw-medium">{district.toUpperCase()}, {state.toUpperCase()}</span>
                      <div className="text-danger small mt-1">⚠️ Cannot be changed later.</div>
                    </td>
                  </tr>

                  <tr><td>8</td><td>Centers (PET / Written)</td><td className="fw-medium">{petCenter.toUpperCase()} / {writtenCenter.toUpperCase()}</td></tr>
                  <tr><td>9</td><td>Gender</td><td className="fw-medium">{gender.toUpperCase()}</td></tr>
                  <tr>
                    <td>10</td><td>Category</td>
                    <td>
                      <span className="fw-medium">{category.toUpperCase()}</span>
                      <div className="text-danger small mt-1">⚠️ Cannot be changed later.</div>
                    </td>
                  </tr>
                  <tr><td>11</td><td>Special Category</td><td className="fw-medium">{specialCategory.toUpperCase()}</td></tr>
                  <tr><td>12</td><td>Education</td><td className="fw-medium">{qualification?.toUpperCase()}</td></tr>
                  <tr><td>13</td><td>LMV with transport endorsement</td><td className="fw-medium">{bonus === "LMV" ? "Yes" : "No"}</td></tr>
                  <tr><td>14</td><td>Email</td><td className="fw-medium">{email.toUpperCase()}</td></tr>
                  <tr><td>15</td><td>Involvement in Criminal Case(s)</td><td className="fw-medium">{criminal}</td></tr>
                </tbody>
              </Table>
            </div>

            <div className="card mt-4 border-primary">
              <div className="card-body bg-light">
                <label htmlFor="otp" className="form-label fw-bold">Enter OTP sent to {phoneNumber}:</label>
                <div className="input-group mb-2">
                  <input type="text" className="form-control" id="otp" value={otp} onChange={(e) => setOtp(e.target.value)} required placeholder="Enter OTP" />
                  <Button variant="outline-primary" style={{ borderColor: '#06213f', color: '#06213f' }} onClick={resendOTP}>
                    Resend OTP
                  </Button>
                </div>
                {uploadMessage && <div className="text-success small fw-medium">{uploadMessage}</div>}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button variant="secondary" onClick={handleClose}>Review Details</Button>
            <Button variant="primary" style={{ backgroundColor: '#06213f', border: 'none' }} onClick={handleOtpSubmit}>Confirm & Submit</Button>
          </Modal.Footer>
        </Modal>

      </form>
    </Card.Body>
  </Card>
</div>
      )}
      <ToastContainer
      position="bottom-center"
      autoClose={false}
      hideProgressBar={true}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="light"
      />
    </div>
  );
};

export default AuthComponent;
